import { useState, useEffect, useCallback, useRef } from 'react'
import Swal from 'sweetalert2'
import commissionConfigService from '../../services/commissionConfigService'

// "Other Main Markets — per market" section of the Commission screen.
// One row per market FAMILY of the sport (trailing number dropped, so
// Next Goal 1.0 … 10.0 share one row). Follow = no row, the sport's
// "Other Main Markets" cell decides; ON / OFF override it for that family.
// Plan: docs/plans/2026-10-21-commission-per-market-family.md
//
// No toolbar of its own: edits are drafts saved / discarded by the page's
// "Save changes" / "Discard" buttons, like the grid above. The page passes
// `register` and gets back { dirty, save, discard } for this sport.

const STATES = [
  { key: 'follow', label: 'Follow', cls: 'info' },
  { key: 'on', label: 'ON', cls: 'success' },
  { key: 'off', label: 'OFF', cls: 'secondary' },
]

const ruleState = (rule) => (rule ? (rule.enabled ? 'on' : 'off') : 'follow')

function CommissionMarketFamilies({ sportId, register }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  // family → { state, percent, label } — committed on the page's Save only
  const [drafts, setDrafts] = useState({})

  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: false,
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await commissionConfigService.getMarketFamilies(sportId)
      if (response.success) {
        setData(response)
        setDrafts({})
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    } finally {
      setLoading(false)
    }
  }, [sportId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  const families = data?.families || []
  const savedOf = (f) => ({
    state: ruleState(f.rule),
    percent: f.rule ? f.rule.percent : data.default_percent,
    label: f.label,
  })
  const current = (f) => drafts[f.family] || savedOf(f)
  const isDirty = (f) => {
    const d = drafts[f.family]
    if (!d) return false
    const s = savedOf(f)
    return d.state !== s.state || (d.state !== 'follow' && Number(d.percent) !== Number(s.percent))
  }
  const setDraft = (f, patch) =>
    setDrafts((prev) => ({ ...prev, [f.family]: { ...current(f), ...patch } }))
  const dirty = families.filter(isDirty)

  // Throws on a bad percent so the page's Save stops before writing anything.
  const save = async () => {
    const items = []
    const resets = []
    for (const f of dirty) {
      const d = current(f)
      if (d.state === 'follow') {
        resets.push(f.family)
        continue
      }
      const percent = Number(d.percent)
      if (!Number.isFinite(percent) || percent < 0 || percent > data.max_percent) {
        throw new Error(`${d.label}: percent must be 0–${data.max_percent}`)
      }
      items.push({ market: f.family, label: d.label, enabled: d.state === 'on', percent })
    }
    for (const family of resets) await commissionConfigService.resetMarketFamily(sportId, family)
    if (items.length) await commissionConfigService.saveMarketFamilies(sportId, items)
    await load()
    return items.length + resets.length
  }

  // Hand the page the latest dirty count / save / discard for this sport.
  const saveRef = useRef(save)
  saveRef.current = save
  useEffect(() => {
    register?.(sportId, {
      dirty: dirty.length,
      save: () => saveRef.current(),
      discard: () => setDrafts({}),
    })
  }, [sportId, dirty.length]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => register?.(sportId, null), [sportId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-center p-3 text-muted">Loading...</div>
  if (!data) return null

  // Same table as the sport grid above it: same columns and a grey group row.
  return (
    <div className="table-responsive mt-3">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr>
            <th style={{ width: 230 }}>Market</th>
            <th style={{ width: 210 }} className="text-center">Commission</th>
            <th style={{ width: 130 }} className="text-center">Percent</th>
            <th style={{ minWidth: 190 }}>Effective</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-light">
            <td colSpan={4} className="font-weight-medium font-size-12 text-muted">
              Other Main Markets — per market
            </td>
          </tr>
          {families.length === 0 && (
            <tr><td colSpan={4} className="text-center text-muted">no markets bet on yet</td></tr>
          )}
          {families.map((f) => {
            const d = current(f)
            const on = d.state === 'follow' ? data.fallback.enabled : d.state === 'on'
            const pct = d.state === 'follow' ? data.fallback.percent : d.percent
            return (
              <tr key={f.family} className={isDirty(f) ? 'table-warning' : ''}>
                <td>
                  <div>{d.label}</div>
                  {f.samples.length > 0 && (
                    <small className="text-muted font-size-11">{f.samples.join(', ')}</small>
                  )}
                </td>
                <td className="text-center">
                  <div className="btn-group btn-group-sm">
                    {STATES.map((st) => (
                      <button
                        key={st.key}
                        type="button"
                        className={`btn btn-sm ${d.state === st.key ? `btn-${st.cls}` : 'btn-outline-secondary'}`}
                        onClick={() => setDraft(f, { state: st.key })}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="text-center">
                  <div className="d-flex align-items-center justify-content-center">
                    <input
                      type="number"
                      min="0"
                      max={data.max_percent}
                      step="0.5"
                      className="form-control form-control-sm"
                      style={{ maxWidth: 80 }}
                      disabled={d.state !== 'on'}
                      value={d.percent}
                      onChange={(e) => setDraft(f, { percent: e.target.value })}
                    />
                    <span className="ml-1">%</span>
                  </div>
                </td>
                <td>
                  {on ? (
                    <span className="badge badge-soft-success">charges {pct}% of net win</span>
                  ) : (
                    <span className="badge badge-soft-secondary">no commission</span>
                  )}
                  {d.state === 'follow' && (
                    <small className="text-muted d-block font-size-11">following Other Main Markets</small>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default CommissionMarketFamilies
